/*
 * Peel Homepage v4 — interactivity.
 * Hero slideshow, scroll reveals, accessories rail, device finder.
 * Loaded only on the index template. Relies on assets/peel-common.js
 * (peelInitScrollReveal, peelInitDeviceSelects) being loaded first.
 */
(function () {
  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var wiredRails = new WeakSet();

  document.addEventListener('DOMContentLoaded', function () {
    /* Rails first: they depend on nothing else here, and peelInitScrollReveal
       comes from a separate file — if that one fails to arrive, the arrows
       should still work rather than be taken down with it. */
    initRails(document);
    initHeroSlideshow();
    peelInitScrollReveal([
      '.peel-trust-bar',
      '.peel-device-finder',
      '.peel-product-grid-section',
      '.peel-accessories-section',
      '.peel-ugc-section',
      '.peel-promo-grid-section',
      '.peel-membership',
      '.peel-setup-section',
      '.peel-founder-section',
      '.peel-lifestyle-section .peel-section-head',
      '.peel-lifestyle-item',
      '.faqs-section'
    ]);
    initDeviceFinder();
    initSetupCluster(document);
  });

  /* The theme editor replaces a section's markup on every settings change,
     which throws away the listeners bound at DOMContentLoaded — the arrows
     go dead the moment a merchant edits the section they are looking at.
     Re-binding the new markup keeps them working through an edit session. */
  document.addEventListener('shopify:section:load', function (event) {
    initRails(event.target);
    initSetupCluster(event.target);
    /* Re-rendered markup arrives with its hero slides parked again, and
       nothing in an edit session fires the load event that would promote
       them — a merchant editing the hero would page through blank slides. */
    promoteDeferredMedia(event.target);
  });

  /* ---- Deferred media ----

     Sections hand peel-media `deferred: true` for an image that sits
     inside the viewport but under something else, where loading="lazy" is
     no help: the browser fetches it at once, against the image the
     shopper is actually looking at. Those tags carry their src and srcset
     in data attributes instead, and this puts them back. srcset first, so
     the candidate list is in place before anything can act on src. */
  function promoteDeferredMedia(root) {
    var parked = (root || document).querySelectorAll('[data-peel-src], [data-peel-srcset]');
    parked.forEach(function (el) {
      var srcset = el.getAttribute('data-peel-srcset');
      var src = el.getAttribute('data-peel-src');
      if (srcset) {
        el.setAttribute('srcset', srcset);
        el.removeAttribute('data-peel-srcset');
      }
      if (src) {
        el.setAttribute('src', src);
        el.removeAttribute('data-peel-src');
      }
    });
  }

  /* ---- Setup bundle: the photo cluster fans out as it reaches view ----

     The cluster used to open on :hover, with a (hover: none) rule to pin it
     open where there is no pointer to hover with. Neither signal survives
     contact with Firefox: it does not re-run hit-testing while a wheel
     scroll moves the page under a still mouse, so scrolling the section
     into view never opened the cluster, and Android builds that report a
     phantom mouse put the phone on the (hover: hover) side of the query,
     where nothing opens it either.

     Arrival is the dependable signal, and the page already reveals
     everything else off it. The collected rest state lives behind the
     attribute stamped here, so a browser that runs none of this is left
     with the arrangement standing rather than a pile nothing opens. */
  function initSetupCluster(root) {
    var clusters = (root || document).querySelectorAll('.peel-cluster');
    if (!clusters.length) return;

    var observer = null;
    if (!reducedMotion && typeof IntersectionObserver !== 'undefined') {
      observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-open');
            observer.unobserve(entry.target);
          });
        },
        /* Deep enough that the fan-out is its own beat rather than one more
           thing happening while the section is still fading up, and that
           the cluster is mostly on screen before it starts — at a fifth
           of a phone's stage the gesture used to be over while the rest
           of it was still below the fold. Shallow enough to still fire on
           a stage taller than the window: the ratio asked for is reachable
           up to a stage of 2.6 viewports, and the tallest here is nearer
           three quarters of one. */
        { rootMargin: '0px 0px -20% 0px', threshold: 0.3 }
      );
    }

    Array.prototype.forEach.call(clusters, function (cluster) {
      if (cluster.hasAttribute('data-peel-cluster')) return;
      /* Nothing to open it with: leave the arrangement as the stylesheet
         has it, fanned out and still. */
      if (!observer) return;

      cluster.setAttribute('data-peel-cluster', '');
      /* Land the collected state before the transitions are armed, so the
         cards are not seen travelling backwards into the pile. */
      void cluster.offsetWidth;
      cluster.classList.add('is-armed');
      observer.observe(cluster);
    });
  }

  /* ---- Device finder: brand and model, from declared compatibility ----
     The list comes from what the store actually sells for (see
     assets/peel-device-map.js); the button carries the chosen phone over to
     the collection, which opens filtered to what fits it. */
  function initDeviceFinder() {
    var cta = document.querySelector('[data-peel-device-cta]');
    var base = cta ? cta.getAttribute('data-peel-device-base') : '';

    peelInitDeviceSelects(
      document.querySelector('[data-peel-brand-select]'),
      document.querySelector('[data-peel-model-select]'),
      {
        onChange: function (device) {
          /* With no results collection set the button is a plain link (the
             theme ships it pointing at the grid below) — leave it alone. */
          if (!cta || !device || !base || base.charAt(0) === '#') return;
          var url = new URL(base, window.location.origin);
          url.searchParams.set('device', device.key);
          cta.href = url.pathname + url.search;
        }
      }
    );
  }

  /* ---- Product rails: each section drives its own scroller ----
     Arrows page by whole cards, clamp at both ends and disable
     themselves there, so the control always reflects what it can do.
     Native overflow scrolling keeps touch swipe working alongside. */
  function initRails(root) {
    var scopeRoot = root && root.querySelectorAll ? root : document;
    var rails = scopeRoot.querySelectorAll('[data-peel-rail]');

    rails.forEach(function (rail) {
      // shopify:section:load can fire for a rail that is already wired (a
      // sibling section reloading, say) — bind each one once. Held off the
      // element rather than in an attribute so a re-render, which hands
      // back markup that has never been bound, is not mistaken for one
      // that has.
      if (wiredRails.has(rail)) return;
      wiredRails.add(rail);

      var scope = rail.closest('[data-peel-rail-scope]') || document;
      var prev = scope.querySelector('[data-peel-rail-prev]');
      var next = scope.querySelector('[data-peel-rail-next]');

      function metrics() {
        var first = rail.firstElementChild;
        var cs = getComputedStyle(rail);
        var gap = parseFloat(cs.columnGap || cs.gap) || 22;
        var card = first ? first.getBoundingClientRect().width : 268;
        /* The width cards are actually read across, not the border box:
           the full-bleed rail carries its container offset as padding
           (up to ~336px at 1920), and counting that as viewable made a
           page two cards wider than the view — one click ran the rail
           to its end instead of advancing a screenful. */
        var viewable = rail.clientWidth
          - (parseFloat(cs.paddingLeft) || 0)
          - (parseFloat(cs.paddingRight) || 0);
        /* Whole cards that fit in view — never less than one. The 1px
           tolerance is for a rail whose cards divide its width exactly
           (the accessories row): a sub-pixel card measurement floors to
           three where four fit, and the arrows would page a card short,
           leaving the row half off the grid. Nothing else can gain a card
           on 1px — that needs most of a card's width of slack. */
        var perPage = Math.max(1, Math.floor((viewable + gap + 1) / (card + gap)));
        return {
          step: (card + gap) * perPage,
          max: Math.max(0, rail.scrollWidth - rail.clientWidth)
        };
      }

      function sync() {
        var max = metrics().max;
        var atStart = rail.scrollLeft <= 2;
        var atEnd = rail.scrollLeft >= max - 2;
        if (prev) prev.disabled = max === 0 || atStart;
        if (next) next.disabled = max === 0 || atEnd;
        rail.classList.toggle('is-scrollable', max > 0);
      }

      function go(dir) {
        var m = metrics();
        if (!m.max) return;
        rail.scrollTo({
          left: Math.max(0, Math.min(m.max, rail.scrollLeft + dir * m.step)),
          behavior: reducedMotion ? 'auto' : 'smooth'
        });
      }

      if (prev) prev.addEventListener('click', function () { go(-1); });
      if (next) next.addEventListener('click', function () { go(1); });
      rail.addEventListener('scroll', sync, { passive: true });

      /* A window resize is not the only thing that changes what fits: the
         cards are resized by the 640px breakpoint, by a web font landing,
         and by the theme editor re-flowing the section around them. The
         observer catches all of those, and unlike a window listener it is
         released with the rail when the editor replaces the section. */
      if (typeof ResizeObserver !== 'undefined') {
        new ResizeObserver(sync).observe(rail);
      } else {
        window.addEventListener('resize', sync);
      }

      // Images landing late change scrollWidth; re-check when they do.
      rail.querySelectorAll('img').forEach(function (img) {
        if (!img.complete) img.addEventListener('load', sync, { once: true });
      });

      sync();
    });
  }

  /* ---- Hero slideshow: crossfade, pause on hover, dot + arrow navigation ---- */
  function initHeroSlideshow() {
    var hero = document.querySelector('[data-peel-hero]');
    if (!hero) return;
    var slides = hero.querySelectorAll('[data-peel-hero-slide]');
    var copies = hero.querySelectorAll('[data-peel-hero-copy]');
    var captions = hero.querySelectorAll('[data-peel-hero-caption]');
    var dots = hero.querySelectorAll('[data-peel-hero-dot]');
    var nav = hero.querySelector('[data-peel-hero-dots]');
    var prev = hero.querySelector('[data-peel-hero-prev]');
    var next = hero.querySelector('[data-peel-hero-next]');

    /* The first slide's video carries `autoplay` so it starts without
       waiting on this deferred script, which means a visitor who has asked
       for reduced motion has to be stopped here — before the single-slide
       bail below, which would otherwise leave it running. They get the
       still underneath, which is the video's poster. */
    if (reducedMotion) {
      hero.querySelectorAll('[data-peel-hero-video]').forEach(function (v) {
        v.removeAttribute('autoplay');
        v.pause();
      });
    }
    if (slides.length < 2) return;

    /* The slides behind the first one are parked until the page has
       finished loading, so the hero the shopper can see is not competing
       for bandwidth with two they cannot. `load` is the signal rather
       than a timer: it means the critical path is already clear, and it
       lands well inside the 5600ms before the first advance. A shopper
       who pages ahead of it gets their slide promoted on the spot, in
       show() below. */
    if (document.readyState === 'complete') {
      promoteDeferredMedia(hero);
    } else {
      window.addEventListener('load', function () {
        promoteDeferredMedia(hero);
      }, { once: true });
    }

    var idx = 0;
    var timer = null;
    var heldByFocus = false;

    /* A slide's video plays only while it is the one showing, and starts
       from its first frame each time, so artwork that builds up is seen
       from the start rather than joined part way through. play() is
       allowed to fail — a muted inline video almost never is refused, but
       one with no source for this width (a phone-only cut on a desktop)
       rejects, and the still underneath is the right answer then anyway. */
    function syncVideo(slide, active, restart) {
      var video = slide.querySelector('[data-peel-hero-video]');
      if (!video) return;
      if (!active || reducedMotion) {
        video.pause();
        return;
      }
      if (restart) {
        try {
          video.currentTime = 0;
        } catch (e) {}
      }
      var playing = video.play();
      if (playing && playing.catch) playing.catch(function () {});
    }

    function show(n) {
      var from = idx;
      idx = (n + slides.length) % slides.length;
      promoteDeferredMedia(slides[idx]);
      slides.forEach(function (el, i) {
        el.classList.toggle('is-active', i === idx);
        syncVideo(el, i === idx, i !== from);
        /* A faded-out slide's link is still in the document, and Tab
           would land on a link nobody can see. */
        var link = el.querySelector('.peel-hero-slide-link');
        if (link) {
          if (i === idx) link.removeAttribute('tabindex');
          else link.setAttribute('tabindex', '-1');
        }
      });
      /* Artwork with its own text asks for the shared overlay to lift
         while it is up (the slide's "Turn off the slide overlay"). */
      hero.classList.toggle('is-bare', slides[idx].hasAttribute('data-peel-hero-bare'));
      copies.forEach(function (el, i) {
        el.classList.toggle('is-active', i === idx);
      });
      captions.forEach(function (el, i) {
        el.classList.toggle('is-active', i === idx);
      });
      dots.forEach(function (d, i) {
        d.classList.toggle('is-active', i === idx);
      });
    }

    function start() {
      if (reducedMotion) return;
      if (heldByFocus) return;
      stop();
      timer = setInterval(function () {
        show(idx + 1);
      }, 5600);
    }

    function stop() {
      if (timer) clearInterval(timer);
      timer = null;
    }

    // A click is a deliberate pick, so restart the dwell from here rather
    // than letting the running interval cut the chosen slide short.
    function goTo(n) {
      show(n);
      start();
    }

    dots.forEach(function (d, i) {
      d.addEventListener('click', function () {
        goTo(i);
      });
    });

    if (prev) {
      prev.addEventListener('click', function () {
        goTo(idx - 1);
      });
    }
    if (next) {
      next.addEventListener('click', function () {
        goTo(idx + 1);
      });
    }

    // Arrow keys page the slideshow once focus is inside the control row,
    // so the arrows are reachable without a pointer.
    if (nav) {
      nav.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          goTo(idx - 1);
        } else if (e.key === 'ArrowRight') {
          e.preventDefault();
          goTo(idx + 1);
        }
      });
    }

    hero.addEventListener('mouseenter', stop);
    hero.addEventListener('mouseleave', start);

    // Hover pauses the slideshow for a mouse user; a keyboard user gets the
    // same courtesy from focus, or the slide moves out from under them
    // between one Tab and the next. Only keyboard focus counts: a click also
    // focuses the button, and hover is already covering that visitor — take
    // the hold off them and the slideshow would never resume.
    function isKeyboardFocus(el) {
      try {
        return !!el && !!el.matches && el.matches(':focus-visible');
      } catch (e) {
        return false;
      }
    }

    hero.addEventListener('focusin', function (e) {
      if (!isKeyboardFocus(e.target)) return;
      heldByFocus = true;
      stop();
    });
    hero.addEventListener('focusout', function (e) {
      if (hero.contains(e.relatedTarget)) return;
      heldByFocus = false;
      start();
    });

    show(0);
    start();
  }

})();
